import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { C, S, getStatusBadgeStyle, formatDate, formatDateTime, calcHours, fmtHours, formatCurrency } from '../styles';
import { fetchLabourRates, calcDayworkSheetCost } from '../labourRates';
import { generateDayworkPDF } from '../utils/generateDayworkPDF';
import { sendEmail } from '../utils/emailService';
import { dayworkSubmissionEmail, dayworkApprovalNotification, dayworkChallengeNotification } from '../utils/emailTemplates';

function matLineTotal(row) {
  return (parseFloat(row.quantity) || 0) * (parseFloat(row.price_per_unit) || 0);
}

function plantLineTotal(row) {
  return (parseFloat(row.quantity) || 0) * (parseFloat(row.rate) || 0);
}

function formatHHMM(timeStr) {
  if (!timeStr) return '—';
  return String(timeStr).substring(0, 5);
}

function dwStatusBadge(status) {
  const map = {
    submitted:  { background: '#fef3c7', color: '#92400e' },
    signed:     { background: '#d1fae5', color: '#065f46' },
    disputed:   { background: '#fee2e2', color: '#991b1b' },
    draft:      { background: '#f3f4f6', color: '#6b7280' },
  };
  return map[status] || { background: '#f3f4f6', color: '#6b7280' };
}

export default function DayworkDetail({ navigate, params }) {
  const { profile } = useAuth();
  const dayworkId = params?.dayworkId;
  const [daywork, setDaywork] = useState(null);
  const [labour, setLabour] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [plant, setPlant] = useState([]);
  const [continuations, setContinuations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendName, setResendName] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendCompany, setResendCompany] = useState('');
  const [resendCc, setResendCc] = useState(false);
  const [resendCcEmail, setResendCcEmail] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resendSending, setResendSending] = useState(false);
  const [resendToast, setResendToast] = useState('');
  const [submissionLog, setSubmissionLog] = useState([]);
  const [signApproval, setSignApproval] = useState(null);
  const [rates, setRates] = useState(null);
  const [instructionDetail, setInstructionDetail] = useState(null);
  const [instructionAttachments, setInstructionAttachments] = useState([]);

  const role = profile?.role || '';

  useEffect(() => {
    if (dayworkId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayworkId]);

  // 8-second loading safety net
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      setLoading(false);
      setError('Loading timed out — please refresh the page or sign in again.');
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const [{ data: dw, error: dwErr }, { data: lab }, { data: mat }, { data: pla }] = await Promise.all([
        supabase
          .from('dayworks')
          .select('*, projects(id, name, reference), instructions(id, reference, type), created_by_profile:profiles!dayworks_created_by_fkey(full_name), signed_by_profile:profiles!dayworks_signed_by_fkey(full_name, company)')
          .eq('id', dayworkId)
          .single(),
        supabase.from('daywork_labour').select('*').eq('daywork_id', dayworkId).order('id'),
        supabase.from('daywork_materials').select('*').eq('daywork_id', dayworkId).order('id'),
        supabase.from('daywork_plant').select('*').eq('daywork_id', dayworkId).order('id'),
      ]);
      if (dwErr) throw dwErr;
      setDaywork(dw);
      setLabour(lab || []);
      setMaterials(mat || []);
      setPlant(pla || []);

      // Load rates for this project
      if (dw?.project_id) {
        fetchLabourRates(dw.project_id).then(setRates).catch(console.error);
      }

      // Load full instruction detail + attachments for PDF
      if (dw?.instruction_id) {
        const [{ data: instr }, { data: attaches }] = await Promise.all([
          supabase
            .from('instructions')
            .select('id, reference, type, date, time, location, gridlines, level, instructed_by_name, instructed_by_role, instruction_method, description, reason_outside_scope')
            .eq('id', dw.instruction_id)
            .single(),
          supabase
            .from('instruction_attachments')
            .select('id, file_name, file_size, file_type, storage_path, created_at')
            .eq('instruction_id', dw.instruction_id)
            .order('created_at'),
        ]);
        setInstructionDetail(instr || null);
        setInstructionAttachments(attaches || []);
      }

      // Load continuation sheets
      if (dw) {
        const { data: conts } = await supabase
          .from('dayworks')
          .select('id, reference, date_of_works, status')
          .eq('continuation_of', dw.id)
          .order('date_of_works');
        setContinuations(conts || []);
      }

      // Load submission history
      const { data: log } = await supabase
        .from('submission_log')
        .select('*, profiles(full_name)')
        .eq('record_type', 'daywork')
        .eq('record_id', dayworkId)
        .order('sent_at', { ascending: false });
      setSubmissionLog(log || []);

      // Load MC portal approval record (stores signer name/company for portal signings)
      const { data: approval } = await supabase
        .from('daywork_approvals')
        .select('full_name_confirmed, company_confirmed, actioned_at')
        .eq('daywork_id', dayworkId)
        .eq('action', 'approved')
        .order('actioned_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setSignApproval(approval || null);
    } catch (err) {
      console.error('DayworkDetail loadData error:', err);
      setError(err.message || 'Failed to load daywork');
    } finally {
      setLoading(false);
    }
  }

  async function handleSign() {
    if (!window.confirm('Sign this daywork sheet? This confirms the works were carried out as described.')) return;
    setProcessing(true);
    setError('');
    try {
      const { error: updateErr } = await supabase
        .from('dayworks')
        .update({ status: 'signed', signed_by: profile.id, signed_at: new Date().toISOString() })
        .eq('id', dayworkId);
      if (updateErr) throw updateErr;
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  }

  async function handleDispute() {
    if (!disputeReason.trim()) { setError('Please provide a reason for disputing this daywork.'); return; }
    setProcessing(true);
    setError('');
    try {
      const { error: updateErr } = await supabase
        .from('dayworks')
        .update({ status: 'disputed', dispute_reason: disputeReason })
        .eq('id', dayworkId);
      if (updateErr) throw updateErr;
      setShowDisputeForm(false);
      setDisputeReason('');
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  }

  function handleAddContinuation() {
    navigate('daywork-new', {
      projectId: daywork.project_id,
      instructionId: daywork.instruction_id,
      continuationOf: daywork.id,
      continuationRef: daywork.reference,
    });
  }

  async function handleSubmitToMC() {
    setProcessing(true);
    setError('');
    try {
      // Step 1: look up MC QS via project_users join
      const { data: puData } = await supabase
        .from('project_users')
        .select('user_id, profiles(id, full_name, email, company, role, approved)')
        .eq('project_id', daywork.project_id);

      let mcQsProfile = (puData || [])
        .map(u => u.profiles)
        .find(p => p && p.role === 'mc_qs' && p.approved === true);

      // Step 2: fall back to requested_project_id on profiles
      if (!mcQsProfile) {
        const { data: mcFromProfile } = await supabase
          .from('profiles')
          .select('id, full_name, email, company')
          .eq('role', 'mc_qs')
          .eq('approved', true)
          .eq('requested_project_id', daywork.project_id);
        mcQsProfile = mcFromProfile?.[0] || null;
      }

      if (!mcQsProfile) {
        setError('No approved MC QS user is assigned to this project. Please assign one in the admin panel first.');
        return;
      }

      const token = crypto.randomUUID();
      const now = new Date().toISOString();
      const portalUrl = `https://proline.project-management.uk/mc-portal/daywork/${dayworkId}?token=${token}`;

      const { error: updateErr } = await supabase
        .from('dayworks')
        .update({
          status: 'submitted',
          submitted_at: now,
          submitted_to_name: mcQsProfile.full_name,
          submitted_to_email: mcQsProfile.email,
          submitted_to_company: mcQsProfile.company || null,
          submitted_by_name: profile.full_name,
        })
        .eq('id', dayworkId);
      if (updateErr) throw updateErr;

      // Notify emails: Proline QS / admin on this project
      const { data: notifyData } = await supabase
        .from('project_users')
        .select('profiles(email, role)')
        .eq('project_id', daywork.project_id);
      const notifyEmails = (notifyData || [])
        .map(u => u.profiles)
        .filter(p => p && (p.role === 'proline_qs' || p.role === 'admin'))
        .map(p => p.email)
        .filter(Boolean);

      const { error: invErr } = await supabase
        .from('mc_invitations')
        .insert({
          project_id: daywork.project_id,
          email: mcQsProfile.email,
          full_name: mcQsProfile.full_name,
          company: mcQsProfile.company || null,
          token,
          type: 'daywork',
          status: 'pending',
          created_by: profile.id,
          item_id: dayworkId,
          item_type: 'daywork',
          notify_emails: notifyEmails,
        });
      if (invErr) throw invErr;

      const updatedDaywork = { ...daywork, submitted_at: now };
      const { subject, htmlBody } = dayworkSubmissionEmail(
        updatedDaywork, daywork.projects, daywork.instructions, mcQsProfile.full_name, portalUrl
      );
      await sendEmail({ to: mcQsProfile.email, subject, htmlBody });
      await sendEmail({ to: 'notifications@prolineinternals.co.uk', subject, htmlBody });

      await supabase.from('submission_log').insert({
        record_type: 'daywork', record_id: dayworkId,
        sent_to_name: mcQsProfile.full_name, sent_to_email: mcQsProfile.email,
        sent_to_company: mcQsProfile.company || null,
        sent_by: profile.id, token, is_resend: false,
      });

      setShowSubmitModal(false);
      setResendToast(`${daywork.reference} submitted to ${mcQsProfile.full_name}${mcQsProfile.company ? ` at ${mcQsProfile.company}` : ''}`);
      setTimeout(() => setResendToast(''), 5000);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  }

  async function handleDownloadPDF() {
    const labourForPdf = labour.map(r => ({
      ...r,
      hours_calculated: r.hours_calculated ?? calcHours(r.start_time, r.finish_time) ?? 0,
    }));
    try {
      await generateDayworkPDF({
        reference: daywork.reference,
        dateOfWorks: daywork.date_of_works,
        status: daywork.status,
        projectName: daywork.projects?.name,
        projectRef: daywork.projects?.reference,
        mcName: daywork.submitted_to_name,
        location: daywork.location,
        level: daywork.level,
        gridlines: daywork.gridlines,
        instructionRef: daywork.instructions?.reference,
        instructionType: daywork.instructions?.type,
        description: daywork.description,
        raisedBy: daywork.submitted_by_name || 'Proline Internals Ltd',
        createdDate: daywork.created_at ? daywork.created_at.split('T')[0] : null,
        labour: labourForPdf,
        materials,
        plant,
        rates,
        mcEmail: daywork.submitted_to_email,
        instructionDetail,
        instructionAttachments,
        mcInstructionAuthority: (() => { try { return daywork.mc_instruction_authority ? JSON.parse(daywork.mc_instruction_authority) : null; } catch (_) { return null; } })(),
        worksDescription: daywork.works_description || null,
        reasonOutsideScope: daywork.reason_outside_scope || null,
      });
    } catch (err) {
      console.error('PDF generation error:', err);
    }
  }

  function openResendModal() {
    setResendName(daywork.submitted_to_name || '');
    setResendEmail(daywork.submitted_to_email || '');
    setResendCompany(daywork.submitted_to_company || '');
    setResendCc(false);
    setResendCcEmail('');
    setResendMessage('Please find the following daywork sheet submitted by Proline Internals Ltd for your review. Please use the secure link below to confirm or challenge the sheet at your earliest convenience.');
    setShowResendModal(true);
  }

  async function handleResend() {
    if (!resendName.trim()) { setError('Recipient name is required.'); return; }
    if (!resendEmail.trim()) { setError('Recipient email is required.'); return; }
    if (!resendCompany.trim()) { setError('Company is required.'); return; }
    setResendSending(true);
    setError('');
    try {
      const token = crypto.randomUUID();
      const portalUrl = `https://proline.project-management.uk/mc-portal/daywork/${dayworkId}?token=${token}`;

      await supabase.from('mc_invitations').insert({
        project_id: daywork.project_id,
        email: resendEmail, full_name: resendName, company: resendCompany,
        token, type: 'daywork', status: 'pending', created_by: profile.id,
        item_id: dayworkId, item_type: 'daywork', notify_emails: [],
      });

      let { subject, htmlBody } = dayworkSubmissionEmail(
        daywork, daywork.projects, daywork.instructions, resendName, portalUrl
      );
      if (resendMessage.trim()) {
        const safeMsg = resendMessage.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/\n/g,'<br/>');
        const noteHtml = `<div style="background:#fffbeb;border-left:4px solid #f59e0b;padding:12px 16px;border-radius:0 6px 6px 0;margin-bottom:20px;"><p style="margin:0;font-size:13px;color:#78350f;line-height:1.6;">${safeMsg}</p></div>`;
        htmlBody = htmlBody.replace('<h2 style=', `${noteHtml}<h2 style=`);
      }
      const toList = resendCc && resendCcEmail.trim() ? [resendEmail, resendCcEmail.trim()] : resendEmail;
      await sendEmail({ to: toList, subject, htmlBody });

      await supabase.from('submission_log').insert({
        record_type: 'daywork', record_id: dayworkId,
        sent_to_name: resendName, sent_to_email: resendEmail, sent_to_company: resendCompany,
        sent_by: profile.id, token, is_resend: true,
      });

      setShowResendModal(false);
      setResendToast(`Email sent to ${resendName} at ${resendEmail}`);
      setTimeout(() => setResendToast(''), 5000);
      const { data: log } = await supabase
        .from('submission_log').select('*, profiles(full_name)')
        .eq('record_type', 'daywork').eq('record_id', dayworkId)
        .order('sent_at', { ascending: false });
      setSubmissionLog(log || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setResendSending(false);
    }
  }

  if (loading) return <div style={S.spinner}>Loading daywork...</div>;
  if (!daywork) return (
    <div style={S.pageContainer}>
      <div style={S.alertError}>{error || 'Daywork not found.'}</div>
      <button onClick={() => navigate('projects', {})} style={{ ...S.btn, ...S.btnGhost }}>← Back</button>
    </div>
  );

  const statusStyle = getStatusBadgeStyle(daywork.status);
  const totalLabourHours = labour.reduce((sum, r) => sum + (calcHours(r.start_time, r.finish_time) || 0), 0);
  const totalMaterials = materials.reduce((sum, r) => sum + matLineTotal(r), 0);
  const totalPlant = plant.reduce((sum, r) => sum + plantLineTotal(r), 0);

  const labourCost = rates
    ? labour.reduce((sum, r) => sum + (r.hours_calculated || calcHours(r.start_time, r.finish_time) || 0) * rates.getRateForTrade(r.trade), 0)
    : null;
  const materialsWithUplift = rates ? totalMaterials * (1 + rates.materialsUplift / 100) : null;
  const plantWithUplift = rates ? totalPlant * (1 + rates.plantUplift / 100) : null;
  const sheetTotal = rates ? calcDayworkSheetCost(labour, materials, plant, rates) : null;

  const isMcQs = role === 'mc_qs';
  const isProlineStaff = role === 'admin' || role === 'proline_sm' || role === 'proline_qs';
  const canResend = isProlineStaff && ['submitted', 'signed', 'disputed'].includes(daywork.status) && !!daywork.submitted_at;
  const canEdit = isProlineStaff && daywork.status === 'draft';
  const canSubmitToMC = isProlineStaff && daywork.status === 'draft';
  const canSign = isMcQs && daywork.status === 'submitted';
  const canDispute = isMcQs && daywork.status === 'submitted';
  const canAddContinuation = isProlineStaff && (daywork.status === 'draft' || daywork.status === 'submitted');

  return (
    <div style={S.pageContainer}>
      <button
        onClick={() => navigate('project-detail', { projectId: daywork.project_id })}
        style={{ ...S.btn, ...S.btnGhost, ...S.btnSm, marginBottom: '16px' }}
      >
        ← Back to Project
      </button>

      {error && <div style={{ ...S.alertError, marginBottom: '16px' }}>{error}</div>}

      {/* Submit confirmation overlay */}
      {showSubmitModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', maxWidth: '480px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#111' }}>Submit to Main Contractor?</h2>
            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.6', margin: '0 0 16px 0' }}>
              This will submit <strong>{daywork.reference}</strong> to the MC QS portal. You will not be able to edit it after submission.
            </p>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px', fontSize: '13px', color: '#374151' }}>
              The MC QS assigned to this project will receive a secure email link to sign or dispute the daywork sheet.
            </div>
            {error && <div style={{ ...S.alertError, marginBottom: '12px' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowSubmitModal(false); setError(''); }} disabled={processing} style={{ padding: '9px 18px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSubmitToMC} disabled={processing} style={{ padding: '9px 18px', background: C.success, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', opacity: processing ? 0.7 : 1 }}>
                {processing ? 'Submitting…' : 'Confirm & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resend modal */}
      {showResendModal && (
        <div style={{ ...S.card, marginBottom: '20px', background: '#fff', border: '2px solid #000' }}>
          <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '700', color: C.text }}>
            Send Submission Email
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px', marginBottom: '12px' }}>
            <div style={S.formGroup}>
              <label style={S.label}>Recipient Name *</label>
              <input style={S.input} value={resendName} onChange={e => setResendName(e.target.value)} placeholder="e.g. John Smith" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Recipient Email *</label>
              <input type="email" style={S.input} value={resendEmail} onChange={e => setResendEmail(e.target.value)} placeholder="qs@contractor.co.uk" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Company *</label>
              <input style={S.input} value={resendCompany} onChange={e => setResendCompany(e.target.value)} placeholder="e.g. Main Contractor Ltd" />
            </div>
          </div>
          <div style={{ ...S.formGroup, marginBottom: '12px' }}>
            <label style={S.label}>Covering Note (optional)</label>
            <textarea
              style={{ ...S.textarea, minHeight: '70px' }}
              value={resendMessage}
              onChange={e => setResendMessage(e.target.value)}
              rows={3}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <input
              type="checkbox" id="dw-resend-cc" checked={resendCc}
              onChange={e => setResendCc(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <label htmlFor="dw-resend-cc" style={{ fontSize: '14px', color: C.text, cursor: 'pointer' }}>Add CC recipient</label>
            {resendCc && (
              <input
                type="email" style={{ ...S.input, maxWidth: '280px' }}
                value={resendCcEmail} onChange={e => setResendCcEmail(e.target.value)}
                placeholder="cc@example.com"
              />
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleResend} disabled={resendSending} style={{ ...S.btn, ...S.btnPrimary, opacity: resendSending ? 0.7 : 1 }}>
              {resendSending ? 'Sending…' : 'Send'}
            </button>
            <button onClick={() => setShowResendModal(false)} style={{ ...S.btn, ...S.btnGhost }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── HEADER PANEL ── */}
      <div style={{ background: '#000000', borderRadius: '8px', padding: '24px 28px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          {/* Left: identity + submission info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '6px 16px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '20px', fontWeight: '700', letterSpacing: '0.08em' }}>
                {daywork.reference}
              </span>
              <span style={{ ...dwStatusBadge(daywork.status), padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', letterSpacing: '0.04em' }}>
                {daywork.status.toUpperCase()}
              </span>
              <span style={{ background: 'rgba(255,255,255,0.1)', color: '#e5e7eb', padding: '5px 12px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                {formatDate(daywork.date_of_works)}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8', display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
              <div><span style={{ color: '#cbd5e1' }}>Project:</span> {daywork.projects?.reference} — {daywork.projects?.name}</div>
              {daywork.instructions?.reference && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#cbd5e1' }}>Instruction:</span>
                  <button onClick={() => navigate('instruction-detail', { instructionId: daywork.instruction_id })}
                    style={{ background: 'rgba(255,255,255,0.1)', color: '#a5b4fc', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontFamily: 'monospace', fontWeight: '700', fontSize: '12px' }}>
                    {daywork.instructions.reference}
                  </button>
                  <span style={{ color: '#6b7280', fontSize: '12px' }}>({daywork.instructions.type})</span>
                </div>
              )}
              {daywork.continuation_of && <div style={{ color: '#6b7280', fontSize: '12px' }}>Continuation sheet</div>}
            </div>
            {daywork.submitted_at && (
              <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.06)', borderRadius: '6px', marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Submitted to Main Contractor</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '4px', fontSize: '13px' }}>
                  {daywork.submitted_to_name && <div><span style={{ color: '#94a3b8' }}>Submitted to: </span><span style={{ color: '#fff', fontWeight: '600' }}>{daywork.submitted_to_name}</span></div>}
                  {daywork.submitted_to_company && <div><span style={{ color: '#94a3b8' }}>Company: </span><span style={{ color: '#e5e7eb' }}>{daywork.submitted_to_company}</span></div>}
                  <div><span style={{ color: '#94a3b8' }}>Date: </span><span style={{ color: '#e5e7eb' }}>{formatDateTime(daywork.submitted_at)}</span></div>
                  {daywork.submitted_by_name && <div><span style={{ color: '#94a3b8' }}>By: </span><span style={{ color: '#e5e7eb' }}>{daywork.submitted_by_name}</span></div>}
                </div>
              </div>
            )}
            {daywork.status === 'signed' && daywork.signed_at && (
              <div style={{ padding: '10px 14px', background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '6px', marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: '#6ee7b7', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Signed — MC Acceptance</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '4px', fontSize: '13px' }}>
                  {(() => {
                    const signerName = signApproval?.full_name_confirmed || daywork.signed_by_profile?.full_name;
                    const signerCompany = signApproval?.company_confirmed || daywork.signed_by_profile?.company;
                    return (
                      <>
                        {signerName
                          ? <div><span style={{ color: '#94a3b8' }}>Signed by: </span><span style={{ color: '#a7f3d0', fontWeight: '600' }}>{signerName}{signerCompany ? ` — ${signerCompany}` : ''}</span></div>
                          : <div><span style={{ color: '#fca5a5', fontWeight: '600' }}>Signer details not recorded</span></div>
                        }
                        <div><span style={{ color: '#94a3b8' }}>Date: </span><span style={{ color: '#a7f3d0' }}>{formatDateTime(daywork.signed_at)}</span></div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
            {daywork.status === 'disputed' && daywork.dispute_reason && (
              <div style={{ padding: '10px 14px', background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '6px', marginTop: '4px' }}>
                <div style={{ fontSize: '11px', color: '#fca5a5', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Dispute Reason</div>
                <div style={{ fontSize: '13px', color: '#fecaca', lineHeight: '1.6' }}>{daywork.dispute_reason}</div>
              </div>
            )}
          </div>
          {/* Right: buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'stretch', minWidth: '210px' }}>
            <button onClick={handleDownloadPDF} style={{ ...S.btn, background: '#374151', color: '#fff', border: 'none', fontSize: '13px', fontWeight: '600', justifyContent: 'center' }}>
              Download PDF
            </button>
            {canResend && (
              <button onClick={openResendModal} style={{ ...S.btn, background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.55)', fontSize: '13px', fontWeight: '600', justifyContent: 'center' }}>
                Resend / Send to Another Recipient
              </button>
            )}
            {canEdit && (
              <button onClick={() => navigate('daywork-edit', { dayworkId })} style={{ ...S.btn, background: 'rgba(255,255,255,0.1)', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.2)', fontSize: '13px' }}>
                Edit Draft
              </button>
            )}
            {canSubmitToMC && (
              <button onClick={() => setShowSubmitModal(true)} disabled={processing} style={{ ...S.btn, background: C.success, color: '#fff', border: 'none', fontSize: '13px', opacity: processing ? 0.7 : 1 }}>
                Submit to MC
              </button>
            )}
            {canAddContinuation && (
              <button onClick={handleAddContinuation} style={{ ...S.btn, background: 'rgba(255,255,255,0.1)', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.2)', fontSize: '13px' }}>
                + Add Continuation Day
              </button>
            )}
            {canSign && (
              <button onClick={handleSign} disabled={processing} style={{ ...S.btn, background: C.success, color: '#fff', border: 'none', fontSize: '13px', opacity: processing ? 0.7 : 1 }}>
                Sign Daywork
              </button>
            )}
            {canDispute && (
              <button onClick={() => setShowDisputeForm(!showDisputeForm)} disabled={processing} style={{ ...S.btn, background: '#dc2626', color: '#fff', border: 'none', fontSize: '13px', opacity: processing ? 0.7 : 1 }}>
                Dispute
              </button>
            )}
          </div>
        </div>

        {showDisputeForm && (
          <div style={{ marginTop: '16px', padding: '16px', background: 'rgba(220,38,38,0.15)', borderRadius: '6px', border: '1px solid rgba(220,38,38,0.3)' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#fca5a5', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Reason for Dispute *</label>
            <textarea style={{ ...S.textarea, background: 'rgba(255,255,255,0.05)', color: '#fff', borderColor: 'rgba(220,38,38,0.4)' }}
              value={disputeReason} onChange={e => setDisputeReason(e.target.value)} rows={3}
              placeholder="Please explain why you are disputing this daywork sheet..." />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button onClick={handleDispute} disabled={processing} style={{ ...S.btn, background: '#dc2626', color: '#fff', opacity: processing ? 0.7 : 1 }}>Confirm Dispute</button>
              <button onClick={() => setShowDisputeForm(false)} style={{ ...S.btn, background: 'rgba(255,255,255,0.1)', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.2)' }}>Cancel</button>
            </div>
          </div>
        )}

      </div>

      {/* Details */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Details</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', fontSize: '14px' }}>
          {daywork.location && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Location:</span> {daywork.location}</div>}
          {daywork.gridlines && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Gridlines:</span> {daywork.gridlines}</div>}
          {daywork.level && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Level / Floor:</span> {daywork.level}</div>}
          <div><span style={{ fontWeight: '600', color: C.textMuted }}>Raised By:</span> {daywork.created_by_profile?.full_name || '—'}</div>
          <div><span style={{ fontWeight: '600', color: C.textMuted }}>Created:</span> {formatDateTime(daywork.created_at)}</div>
        </div>
        {daywork.description && (
          <div style={{ marginTop: '12px', fontSize: '14px', color: C.text, lineHeight: '1.6' }}>
            <span style={{ fontWeight: '600', color: C.textMuted }}>Description: </span>
            {daywork.description}
          </div>
        )}
      </div>

      {/* Authority for Works */}
      {daywork.mc_instruction_authority && (() => {
        let auth = null;
        try { auth = JSON.parse(daywork.mc_instruction_authority); } catch (_) {}
        if (!auth) return null;
        return (
          <div style={{ ...S.card, marginBottom: '20px' }} key="authority">
            <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Authority for These Works</h3>
            <div style={{ background: '#f8fafc', border: '1px solid #d1d5db', borderRadius: '6px', padding: '14px 16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', fontSize: '14px' }}>
                {auth.reference && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Reference:</span> <span style={{ fontFamily: 'monospace', fontWeight: '700' }}>{auth.reference}</span></div>}
                {auth.type && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Type:</span> {auth.type}</div>}
                {auth.date && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Date:</span> {formatDate(auth.date)}</div>}
                {auth.time && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Time:</span> {auth.time}</div>}
                {auth.instructed_by_name && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Instructed by:</span> {auth.instructed_by_name}{auth.instructed_by_role ? ` (${auth.instructed_by_role})` : ''}</div>}
                {auth.instruction_method && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Method:</span> {auth.instruction_method}</div>}
                {auth.main_contractor && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Main Contractor:</span> {auth.main_contractor}</div>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Works Description */}
      {daywork.works_description && (
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '10px' }}>Description of Works to be Carried Out</h3>
          <p style={{ fontSize: '14px', color: C.text, lineHeight: '1.6', margin: 0 }}>{daywork.works_description}</p>
        </div>
      )}

      {/* Reason Outside Scope */}
      {daywork.reason_outside_scope && (
        <div style={{ ...S.card, marginBottom: '20px', borderLeft: '4px solid #f59e0b' }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '10px' }}>Reason Works Are Outside Subcontract Scope</h3>
          <p style={{ fontSize: '14px', color: C.text, lineHeight: '1.6', margin: 0 }}>{daywork.reason_outside_scope}</p>
        </div>
      )}

      {/* Continuation sheets */}
      {continuations.length > 0 && (
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Continuation Sheets</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {continuations.map(c => {
              const st = getStatusBadgeStyle(c.status);
              return (
                <div
                  key={c.id}
                  onClick={() => navigate('daywork-detail', { dayworkId: c.id })}
                  style={{ padding: '8px 12px', background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <span style={{ fontWeight: '700', color: C.accent, fontFamily: 'monospace', fontSize: '13px' }}>{c.reference}</span>
                  <span style={{ fontSize: '12px', color: C.textMuted }}>{formatDate(c.date_of_works)}</span>
                  <span style={{ ...S.badge, ...st }}>{c.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Labour */}
      {labour.length > 0 && (
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '16px' }}>Labour</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Operative</th>
                  <th style={S.th}>Trade</th>
                  <th style={S.th}>Start</th>
                  <th style={S.th}>Finish</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Hours</th>
                  {rates && <th style={{ ...S.th, textAlign: 'right' }}>Rate</th>}
                  {rates && <th style={{ ...S.th, textAlign: 'right' }}>Cost</th>}
                  <th style={S.th}>Activity</th>
                </tr>
              </thead>
              <tbody>
                {labour.map(row => {
                  const hours = row.hours_calculated || calcHours(row.start_time, row.finish_time) || 0;
                  const rate = rates ? rates.getRateForTrade(row.trade) : null;
                  const cost = rate !== null ? hours * rate : null;
                  return (
                    <tr key={row.id}>
                      <td style={{ ...S.td, fontWeight: '600' }}>{row.operative_name || '—'}</td>
                      <td style={S.td}>{row.trade || '—'}</td>
                      <td style={S.td}>{formatHHMM(row.start_time)}</td>
                      <td style={S.td}>{formatHHMM(row.finish_time)}</td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: '600', color: C.success }}>
                        {hours > 0 ? `${fmtHours(hours)}h` : '—'}
                      </td>
                      {rates && <td style={{ ...S.td, textAlign: 'right', fontSize: '13px', color: C.textMuted }}>£{(rate || 0).toFixed(2)}/hr</td>}
                      {rates && <td style={{ ...S.td, textAlign: 'right', fontWeight: '600', color: C.text }}>{cost !== null && cost > 0 ? formatCurrency(cost) : '—'}</td>}
                      <td style={{ ...S.td, color: C.textMuted, fontSize: '13px' }}>{row.activity_note || '—'}</td>
                    </tr>
                  );
                })}
                <tr style={{ background: '#f0fdf4' }}>
                  <td colSpan={4} style={{ ...S.td, fontWeight: '700', color: C.success }}>Total Labour Hours</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: '700', color: C.success }}>
                    {fmtHours(totalLabourHours)}h
                  </td>
                  {rates && <td style={S.td}></td>}
                  {rates && <td style={{ ...S.td, textAlign: 'right', fontWeight: '700', color: C.success }}>{labourCost !== null ? formatCurrency(labourCost) : ''}</td>}
                  <td style={S.td}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Materials */}
      {materials.length > 0 && (
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '16px' }}>Materials</h3>
          <div style={S.tableWrapper}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Description</th>
                  <th style={S.th}>Quantity</th>
                  <th style={S.th}>Unit</th>
                  <th style={S.th}>Rate (£)</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {materials.map(m => (
                  <tr key={m.id}>
                    <td style={S.td}>{m.description}</td>
                    <td style={S.td}>{m.quantity}</td>
                    <td style={S.td}>{m.unit || '—'}</td>
                    <td style={S.td}>£{parseFloat(m.price_per_unit || 0).toFixed(2)}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: '600' }}>£{matLineTotal(m).toFixed(2)}</td>
                  </tr>
                ))}
                <tr style={{ background: '#f0fdf4' }}>
                  <td colSpan={4} style={{ ...S.td, fontWeight: '700', color: C.success }}>Total Materials</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: '700', color: C.success }}>£{totalMaterials.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Plant */}
      {plant.length > 0 && (
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '16px' }}>Plant & Equipment</h3>
          <div style={S.tableWrapper}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Description</th>
                  <th style={S.th}>Qty</th>
                  <th style={S.th}>Unit</th>
                  <th style={S.th}>Rate</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {plant.map(p => (
                  <tr key={p.id}>
                    <td style={S.td}>{p.description}</td>
                    <td style={S.td}>{p.quantity}</td>
                    <td style={S.td}>{p.unit || '—'}</td>
                    <td style={S.td}>£{parseFloat(p.rate || 0).toFixed(2)}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: '600' }}>£{plantLineTotal(p).toFixed(2)}</td>
                  </tr>
                ))}
                <tr style={{ background: '#f0fdf4' }}>
                  <td colSpan={4} style={{ ...S.td, fontWeight: '700', color: C.success }}>Total Plant</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: '700', color: C.success }}>£{totalPlant.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary */}
      {(labour.length > 0 || materials.length > 0 || plant.length > 0) && (
        <div style={{ ...S.card, marginBottom: '20px', border: `2px solid ${C.accent}` }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Summary</h3>
          <div style={{ fontSize: '14px' }}>
            {labour.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.textMuted }}>Labour ({fmtHours(totalLabourHours)} hrs)</span>
                <span style={{ fontWeight: '600' }}>{labourCost !== null ? formatCurrency(labourCost) : `${fmtHours(totalLabourHours)} hrs`}</span>
              </div>
            )}
            {materials.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.textMuted }}>Materials {rates && rates.materialsUplift > 0 ? `(inc. ${rates.materialsUplift}% uplift)` : '(at cost)'}</span>
                <span style={{ fontWeight: '600' }}>{materialsWithUplift !== null ? formatCurrency(materialsWithUplift) : `£${totalMaterials.toFixed(2)}`}</span>
              </div>
            )}
            {plant.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.textMuted }}>Plant {rates && rates.plantUplift > 0 ? `(inc. ${rates.plantUplift}% uplift)` : '(at cost)'}</span>
                <span style={{ fontWeight: '600' }}>{plantWithUplift !== null ? formatCurrency(plantWithUplift) : `£${totalPlant.toFixed(2)}`}</span>
              </div>
            )}
            {sheetTotal !== null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0 0' }}>
                <span style={{ fontWeight: '700', fontSize: '15px' }}>TOTAL</span>
                <span style={{ fontWeight: '700', fontSize: '18px', color: C.accent }}>{formatCurrency(sheetTotal)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Submission History */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
          <span style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em', color: '#64748b', textTransform: 'uppercase' }}>Submission History</span>
        </div>
        {submissionLog.length === 0 ? (
          <div style={{ padding: '14px 16px', fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>Not yet submitted</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...S.table, margin: 0, borderRadius: 0 }}>
              <thead>
                <tr>
                  <th style={{ ...S.th, fontSize: '11px' }}>Date &amp; Time</th>
                  <th style={{ ...S.th, fontSize: '11px' }}>Sent By</th>
                  <th style={{ ...S.th, fontSize: '11px' }}>Sent To</th>
                </tr>
              </thead>
              <tbody>
                {submissionLog.map(entry => (
                  <tr key={entry.id}>
                    <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: '13px' }}>{formatDateTime(entry.sent_at)}</td>
                    <td style={{ ...S.td, fontSize: '13px' }}>{entry.profiles?.full_name || '—'}</td>
                    <td style={S.td}>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>{entry.sent_to_name}{entry.sent_to_company ? `, ${entry.sent_to_company}` : ''}</div>
                      <div style={{ fontSize: '12px', color: C.textMuted }}>{entry.sent_to_email}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      {resendToast && (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', background: C.success, color: '#fff', padding: '12px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 1000 }}>
          ✓ {resendToast}
        </div>
      )}
    </div>
  );
}
